import { albumQualityLabels } from '../domain/library';
import { albumDescription, albumJsonLd } from '../domain/albumMetadata';
import { albumPath, entityPath, parseAppPath, slugify } from '../domain/routes';
import type { AlbumSummary } from '../types';

export function updatePageMetadata(route: ReturnType<typeof parseAppPath>, albums: AlbumSummary[]) {
  const origin = 'https://flac.banast.as';
  let path = '/';
  let title = 'flac.banast.as — Digital Music Library';
  let description = `Explore Bill Anastas's ${albums.length.toLocaleString()}-album digital music library by artist, label, genre, track title, format, and resolution.`;
  let missing = false;
  let image: string | null = null;
  let jsonLd: Record<string, unknown> | null = null;
  if (route.page === 'album') {
    const album = albums.find((item) => item.id === route.id);
    if (album) {
      path = albumPath(album); title = `${album.title} by ${album.artist} — flac.banast.as`;
      description = albumDescription(album);
      image = album.cover ? `${origin}${album.cover}` : null;
      jsonLd = albumJsonLd(album, origin + path);
    } else missing = true;
  } else if (route.page === 'entity') {
    const type = route.entityType;
    const values = type === 'artist' ? albums.map((album) => album.artist) : type === 'label' ? albums.map((album) => album.label || '') : type === 'genre' ? albums.flatMap((album) => album.genres) : albums.flatMap(albumQualityLabels);
    const name = values.find((value) => value && slugify(value) === route.slug);
    if (name) { path = entityPath(type, name); title = `${name} — flac.banast.as`; description = `Owned albums connected to ${name} in Bill Anastas's digital music library.`; }
    else missing = true;
  } else if (route.page !== 'explore' && route.page !== 'not-found') {
    path = `/${route.page}`;
    title = `${route.page === 'collection' ? 'Digital music collection' : route.page === 'pick' ? 'Pick an album' : 'Library statistics'} — flac.banast.as`;
    description = route.page === 'collection' ? 'Browse and filter the digital music library by artist, label, genre, decade, format, and resolution.' : route.page === 'pick' ? 'Choose an album from the digital music library with optional filters and local pick history.' : 'Explore library formats, resolutions, storage, duration, and catalog health.';
  } else if (route.page === 'not-found') missing = true;
  if (missing) { title = 'Page not found — flac.banast.as'; description = 'This path is not represented in the current digital music library.'; }
  document.title = title;
  const meta = (attribute: 'name' | 'property', name: string, content: string | null) => {
    let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`);
    if (content === null) { element?.remove(); return; }
    if (!element) { element = document.createElement('meta'); element.setAttribute(attribute, name); document.head.append(element); }
    element.content = content;
  };
  meta('name', 'description', description);
  meta('name', 'robots', missing ? 'noindex, follow' : 'index, follow, max-image-preview:large');
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (missing) canonical?.remove();
  else { if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.append(canonical); } canonical.href = origin + path; }
  for (const prefix of ['og', 'twitter']) {
    const attribute = prefix === 'og' ? 'property' : 'name';
    meta(attribute, `${prefix}:title`, title); meta(attribute, `${prefix}:description`, description); meta(attribute, `${prefix}:image`, image);
  }
  meta('property', 'og:type', route.page === 'album' && !missing ? 'music.album' : 'website');
  meta('property', 'og:url', missing ? null : origin + path);
  meta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
  document.head.querySelectorAll('#page-jsonld, #album-jsonld').forEach((element) => element.remove());
  if (!missing) {
    const script = document.createElement('script'); script.id = 'page-jsonld'; script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd || { '@context': 'https://schema.org', '@type': 'CollectionPage', name: title, description, url: origin + path });
    document.head.append(script);
  }
}
